import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2 } from "lucide-react";
import { Reveal } from "./Reveal";
import { trackGenerateLead } from "../analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass =
  "w-full rounded-none border border-black/20 bg-white px-4 py-2.5 text-sm text-royal outline-none transition-[border-color,box-shadow] duration-200 placeholder:text-slate-400 focus:border-royal focus:ring-2 focus:ring-royal/30 lg:py-2";

export const Estimate = () => {
  const [form, setForm] = useState({ name: "", address: "", phone: "", notes: "", company: "" });
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/estimates`, form);
      trackGenerateLead();
      setDone(true);
      toast.success("Estimate request sent — we'll be in touch soon.");
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(
        typeof detail === "string" ? detail : "Something went wrong. Please call us instead."
      );
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="schedule" data-testid="estimate-section" className="noise-overlay flex min-h-[100svh] flex-col justify-center py-10 lg:min-h-screen lg:py-14">
      <div className="mx-auto max-w-2xl px-6">
        <Reveal delay={0.15}>
          <div className="border border-black bg-white p-5 shadow-[8px_8px_0_0_#003399] sm:p-8 lg:p-6">
            {done ? (
              <div data-testid="estimate-success-panel" className="flex flex-col items-start gap-4">
                <CheckCircle2 className="h-10 w-10 text-royal" />
                <h3 className="font-display text-2xl font-bold uppercase tracking-tight text-royal">
                  Request received
                </h3>
                <p className="text-sm leading-relaxed text-slate-600">
                  Thanks, {form.name.split(" ")[0] || "neighbor"} — we&rsquo;ve
                  got your request and will reach out shortly to schedule your
                  free estimate at {form.address || "your property"}.
                </p>
                <button
                  data-testid="estimate-another-button"
                  onClick={() => {
                    setDone(false);
                    setForm({ name: "", address: "", phone: "", notes: "", company: "" });
                  }}
                  className="mt-2 border border-royal px-6 py-3 font-mono text-xs uppercase tracking-wider text-royal transition-colors duration-200 hover:bg-royal hover:text-white"
                >
                  Send another request
                </button>
              </div>
            ) : (
              <form data-testid="estimate-form" onSubmit={submit} className="space-y-3">
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={set("company")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  data-testid="estimate-company-honeypot"
                  style={{ position: "absolute", left: "-9999px", width: "1px", height: "1px", opacity: 0 }}
                />
                <div>
                  <label htmlFor="estimate-name" className="mb-1 block font-mono text-xs font-bold uppercase tracking-[0.2em] text-royal/70">
                    Full Name *
                  </label>
                  <input
                    id="estimate-name"
                    data-testid="estimate-name-input"
                    required
                    value={form.name}
                    onChange={set("name")}
                    placeholder="Full Name"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="estimate-phone" className="mb-1 block font-mono text-xs font-bold uppercase tracking-[0.2em] text-royal/70">
                    Phone Number *
                  </label>
                  <input
                    id="estimate-phone"
                    data-testid="estimate-phone-input"
                    required
                    type="tel"
                    value={form.phone}
                    onChange={set("phone")}
                    placeholder="Phone Number"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="estimate-address" className="mb-1 block font-mono text-xs font-bold uppercase tracking-[0.2em] text-royal/70">
                    Property Address *
                  </label>
                  <input
                    id="estimate-address"
                    data-testid="estimate-address-input"
                    required
                    value={form.address}
                    onChange={set("address")}
                    placeholder="Property Address"
                    className={inputClass}
                  />
                </div>
                <div>
                  <label htmlFor="estimate-notes" className="mb-1 block font-mono text-xs font-bold uppercase tracking-[0.2em] text-royal/70">
                    Notes (optional)
                  </label>
                  <textarea
                    id="estimate-notes"
                    data-testid="estimate-notes-input"
                    rows={3}
                    value={form.notes}
                    onChange={set("notes")}
                    placeholder="Tell us about the work — driveway size, repairs needed, timing…"
                    className={inputClass}
                  />
                </div>
                <button
                  data-testid="estimate-submit-button"
                  type="submit"
                  disabled={sending}
                  className="flex w-full items-center justify-center gap-3 bg-black px-8 py-3 font-mono text-sm font-bold uppercase tracking-wider text-white transition-colors duration-200 hover:bg-black/75 disabled:opacity-60"
                >
                  {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {sending ? "Sending…" : "Request Free Estimate"}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};
